"use client";
import Link from "next/link";
import { ArrowRight, Cpu, Telescope, Activity, Shield, Brain } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { orgStructure as data } from "@/lib/data/leadership";

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = { Engineering: Cpu, Strategy: Telescope, Operations: Activity, Security: Shield, Research: Brain };
const accentColors = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)"];

export function OrgStructure() {
  return (
    <Section id="org-structure" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-amber)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl p-7">
            <span className="eyebrow-tiny text-[var(--color-amber)]">Core Divisions</span>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.divisions.map((d, i) => {
                const Icon = iconMap[d.title] ?? Cpu;
                const accent = accentColors[i % accentColors.length]!;
                return (
                  <div key={d.title} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: `color-mix(in oklch, ${accent} 10%, transparent)` }}>
                    <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                      <Icon className="h-4 w-4" style={{ color: accent }} />
                    </span>
                    <div>
                      <div className="font-semibold text-sm">{d.title}</div>
                      <div className="text-xs text-[rgb(var(--fg-muted))]">{d.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-7 space-y-4">
              {data.decisionMaking.map((d, i) => (
                <div key={i}>
                  <span className="eyebrow-tiny text-[var(--color-amber)]">{d.title}</span>
                  <p className="mt-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{d.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 rounded-xl" style={{ background: `color-mix(in oklch, var(--color-crimson) 8%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-crimson) 25%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: "var(--color-crimson)" }}>{data.culturalDna.title}</span>
              <p className="mt-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{data.culturalDna.body}</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={400}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
